import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as RunbookIcon,
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  HealthAndSafety as HealthIcon,
  History as HistoryIcon,
  OpenInNew as OpenIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  FolderZip as EvidencePackIcon,
  Assignment as AuditIcon,
} from '@mui/icons-material';
import { api, endpoints } from '../../services/api';
import { reportsApi, type EvidencePackResponse } from '../../services/reportsApi';
import { copilotApi } from '../../services/copilotApi';
import ReportVisuals from '../../components/reports/ReportVisuals';
import { getOpsCopilotMode, setOpsCopilotMode, type OpsCopilotMode } from '../../services/opsCopilotMode';
import { buildScopedIncidentsPath } from '../../utils/incidentsNavigation';
import { incidentTypeLabel } from '../incidents/incidentTypeLabels';
import {
  fetchEdgeHealth,
  fetchOpsRunbookTemplates,
  getOpsRunbookExecutionServer,
  listOpsCopilotExecutions,
  listOpsRunbookExecutionsServer,
  normalizeOpsRunbookExecution,
  resolveGuidedRunbookHref,
  runOpsRunbook,
  type EdgeHealthResponse,
  type OpsRunbookExecution,
  type OpsRunbookTemplate,
  type RecommendedAction,
} from '../../services/opsCopilot';
import { useAuth } from '../../contexts/AuthContext';

type Device = {
  device_id: string;
  name: string;
  ip_address?: string;
};

/** Extract a human-readable state summary from step result data (current vs desired, drift, etc.) */
function stepResultSummary(stepId: string, result: { ok?: boolean; status?: string; data?: any; error?: string }): string {
  const d = result?.data;
  const status = result?.status || (result?.ok ? 'succeeded' : 'failed');
  if (result?.error) return `${status} • ${String(result.error).slice(0, 80)}`;

  switch (stepId) {
    case 'release_status': {
      const drift = d?.drift;
      const actual = drift?.actual;
      const expected = drift?.expected;
      const current = actual?.current_release_id ?? actual?.runtime_image_digest_token ?? '—';
      const target = expected?.release_id ?? expected?.image_digest_token ?? drift?.target_release_id ?? '—';
      if (drift?.ok) return `${status} • no drift (current=${String(current).slice(0, 20)}…)`;
      const reasons = Array.isArray(drift?.reasons) ? drift.reasons.join(', ') : drift?.reasons ?? '';
      return `${status} • current=${String(current).slice(0, 16)}… → desired=${String(target).slice(0, 16)}…${reasons ? ` • ${reasons}` : ''}`;
    }
    case 'preflight': {
      const desired = d?.desired_release_id ?? '—';
      const checks = Array.isArray(d?.checks) ? d.checks : [];
      const failed = checks.filter((c: any) => c?.ok === false);
      const failedIds = failed.map((c: any) => c?.id ?? c?.title).filter(Boolean).slice(0, 3);
      if (d?.ok) return `${status} • desired=${desired} • all checks passed`;
      return `${status} • desired=${desired} • failed: ${failedIds.join(', ') || 'precheck'}`;
    }
    case 'set_release':
      return `${status} • desired=${d?.desired_release_id ?? '—'}`;
    case 'apply_update':
      return `${status} • ${d?.status ?? 'triggered'}${d?.job_id ? ` (job=${String(d.job_id).slice(0, 12)}…)` : ''}`;
    case 'release_agent_poll':
      return `${status} • ${d?.status ?? 'requested'}`;
    case 'proof_before':
    case 'proof_after': {
      const domains = d?.domains;
      if (!domains) return `${status}`;
      const parts: string[] = [];
      for (const [k, v] of Object.entries(domains as Record<string, any>)) {
        if (v?.ok === true) parts.push(`${k}: ok`);
        else if (v?.ok === false) parts.push(`${k}: drift`);
      }
      return `${status} • ${parts.length ? parts.join(', ') : '—'}`;
    }
    case 'sync': {
      const r = d?.result;
      const rev = r?.current_revision ?? r?.desired_revision;
      const changed = r?.changed === true ? 'changed' : 'no change';
      return `${status} • ${changed}${typeof rev === 'number' ? ` • rev=${rev}` : ''}`;
    }
    case 'run_preflight': {
      const report = d?.report ?? d;
      const verdict = report?.verdict ?? '—';
      const score = report?.score;
      const blockers = Array.isArray(report?.blockers) ? report.blockers : [];
      const pkg = report?.report_package;
      const pkgParts: string[] = [];
      if (pkg?.zones?.length) pkgParts.push(`${pkg.zones.length} zones`);
      if (pkg?.policies?.length) pkgParts.push(`${pkg.policies.length} policies`);
      if (pkg?.incidents?.total != null) pkgParts.push(`${pkg.incidents.total} incidents`);
      const pkgStr = pkgParts.length ? ` • ${pkgParts.join(', ')}` : '';
      if (report?.verdict) return `${status} • ${verdict}${typeof score === 'number' ? ` (score ${score})` : ''}${blockers.length ? ` • ${blockers.length} blocker(s)` : ''}${pkgStr}`;
      return status;
    }
    default:
      return status;
  }
}

function firstSearchParam(searchParams: URLSearchParams, keys: string[]): string {
  for (const key of keys) {
    const value = String(searchParams.get(key) || '').trim();
    if (value) return value;
  }
  return '';
}

function toDateInputValue(value?: string): string {
  const text = String(value || '').trim();
  if (!text) return '';
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

const OpsCopilotRunbooks: React.FC = () => {
  const navigate = useNavigate();
  const { effectiveUser: user } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canWriteAudit = capabilities.includes('audit:write');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDeviceId = firstSearchParam(searchParams, ['device_id', 'device']);
  const requestedRunbookId = firstSearchParam(searchParams, ['runbook']);
  const guidedSiteId = firstSearchParam(searchParams, ['site_id', 'site']);
  const guidedZoneId = firstSearchParam(searchParams, ['zone_id', 'zone']);
  const guidedIncidentType = firstSearchParam(searchParams, ['incident_type']);
  const guidedFromDate = firstSearchParam(searchParams, ['from_date', 'from']);
  const guidedToDate = firstSearchParam(searchParams, ['to_date', 'to']);
  const guidedFocus = firstSearchParam(searchParams, ['focus']);
  const guidedWorkflow = firstSearchParam(searchParams, ['workflow']);
  const reviewIncidentId = firstSearchParam(searchParams, ['incident_id']);
  const reviewCaseId = firstSearchParam(searchParams, ['case_id']) || reviewIncidentId;
  const reviewProofPackageId = firstSearchParam(searchParams, ['proof_package_id']);
  const reviewSource = firstSearchParam(searchParams, ['source']);
  const reviewProofSurface = firstSearchParam(searchParams, ['proof_surface']);
  const autoOpenedRunbookRef = useRef<string>('');

  const [devices, setDevices] = useState<Device[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);

  const [templates, setTemplates] = useState<OpsRunbookTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  const [executions, setExecutions] = useState<OpsRunbookExecution[]>([]);
  const [selectedExecution, setSelectedExecution] = useState<OpsRunbookExecution | null>(null);
  const [execDialogOpen, setExecDialogOpen] = useState(false);

  const [runningId, setRunningId] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const [edgeHealth, setEdgeHealth] = useState<EdgeHealthResponse | null>(null);
  const [edgeHealthLoading, setEdgeHealthLoading] = useState(false);
  const [edgeHealthError, setEdgeHealthError] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTemplate, setDrawerTemplate] = useState<OpsRunbookTemplate | null>(null);
  /** Pre-filled params when opening from recommended action (e.g. default_inputs) */
  const [drawerPrefillParams, setDrawerPrefillParams] = useState<Record<string, any> | null>(null);
  /** Extra params for runbooks that need them (e.g. desired_release_id for device_update_complete) */
  const [drawerExtraParams, setDrawerExtraParams] = useState<Record<string, any>>({});

  const [modeExpanded, setModeExpanded] = useState(false);
  const [mode, setMode] = useState<OpsCopilotMode>(() => getOpsCopilotMode());
  const [reason, setReason] = useState<string>('');
  const [embeddedEnabled, setEmbeddedEnabled] = useState<boolean>(false);

  const [evidencePackExpanded, setEvidencePackExpanded] = useState(false);
  const [executionJsonExpanded, setExecutionJsonExpanded] = useState(false);
  const [evidencePackFromDate, setEvidencePackFromDate] = useState(() => {
    const seeded = toDateInputValue(guidedFromDate);
    if (seeded) return seeded;
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [evidencePackToDate, setEvidencePackToDate] = useState(() => toDateInputValue(guidedToDate) || new Date().toISOString().slice(0, 10));
  const [evidencePackResult, setEvidencePackResult] = useState<EvidencePackResponse | null>(null);
  const [evidencePackLoading, setEvidencePackLoading] = useState(false);
  const [evidencePackError, setEvidencePackError] = useState<string | null>(null);
  const [trustPackLoading, setTrustPackLoading] = useState(false);
  const [trustPackError, setTrustPackError] = useState<string | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.id === requestedRunbookId) || null,
    [requestedRunbookId, templates],
  );

  const selectedDeviceLabel = useMemo(() => {
    const match = devices.find((device) => String(device.device_id || '').trim() === selectedDeviceId);
    const name = String(match?.name || '').trim();
    if (name && name !== selectedDeviceId) return `${name} (${selectedDeviceId})`;
    return selectedDeviceId || 'this device';
  }, [devices, selectedDeviceId]);

  const reviewOriginLabel = useMemo(
    () => (reviewSource === 'proof' ? 'proof workspace' : 'incident review'),
    [reviewSource],
  );

  const reviewReturnHref = useMemo(() => {
    if (reviewSource === 'proof' && (reviewCaseId || reviewProofPackageId || reviewProofSurface)) {
      const params = new URLSearchParams();
      if (reviewCaseId) params.set('case_id', reviewCaseId);
      if (reviewIncidentId) params.set('incident_id', reviewIncidentId);
      if (reviewProofPackageId) params.set('proof_package_id', reviewProofPackageId);
      if (reviewProofSurface) params.set('surface', reviewProofSurface);
      return `/acceptance/proof?${params.toString()}`;
    }
    if (reviewIncidentId) {
      return buildScopedIncidentsPath(`/incidents?incident_id=${encodeURIComponent(reviewIncidentId)}&review=1`);
    }
    const params = new URLSearchParams();
    params.set('tab', 'open');
    params.set('table_mode', 'grouped');
    params.set('focus', guidedFocus || 'queue');
    if (guidedSiteId) params.set('site', guidedSiteId);
    if (selectedDeviceId) params.set('device_id', selectedDeviceId);
    if (guidedZoneId) params.set('zone_id', guidedZoneId);
    if (guidedIncidentType) params.set('incident_type', guidedIncidentType);
    if (guidedFromDate) params.set('from_date', guidedFromDate);
    if (guidedToDate) params.set('to_date', guidedToDate);
    if (guidedWorkflow) params.set('workflow', guidedWorkflow);
    return buildScopedIncidentsPath(`/incidents?${params.toString()}`);
  }, [
    guidedFocus,
    guidedFromDate,
    guidedIncidentType,
    guidedSiteId,
    guidedToDate,
    guidedWorkflow,
    guidedZoneId,
    reviewCaseId,
    reviewIncidentId,
    reviewProofPackageId,
    reviewProofSurface,
    reviewSource,
    selectedDeviceId,
  ]);

  const reviewReturnLabel = useMemo(
    () => (reviewSource === 'proof' && (reviewCaseId || reviewProofPackageId || reviewProofSurface) ? 'Back to proof workspace' : 'Back to incident review'),
    [reviewCaseId, reviewProofPackageId, reviewProofSurface, reviewSource],
  );

  const reviewHandoffTitle = useMemo(() => {
    if (!selectedTemplate) return `Opened from ${reviewOriginLabel}`;
    if (selectedTemplate.id === 'incident_evidence_recover') return `Opened from ${reviewOriginLabel} to recover missing evidence`;
    if (selectedTemplate.kind === 'guided') return `Opened from ${reviewOriginLabel} to continue ${selectedTemplate.title.toLowerCase()}`;
    return `Opened from ${reviewOriginLabel} to run ${selectedTemplate.title.toLowerCase()}`;
  }, [reviewOriginLabel, selectedTemplate]);

  const reviewHandoffSummary = useMemo(() => {
    if (!selectedTemplate) return null;
    const incidentLabel = guidedIncidentType ? incidentTypeLabel(guidedIncidentType) : 'this recurring risk';
    const zoneLabel = guidedZoneId ? `zone ${guidedZoneId}` : 'the current area';
    if (selectedTemplate.id === 'incident_evidence_recover') {
      return `Use this page to recover clips, snapshots, or uploads for ${incidentLabel} on ${selectedDeviceLabel}. This stays tied to the same case flow, but it switches from judging the incident to repairing the operational evidence path behind it.`;
    }
    if (selectedTemplate.kind === 'guided') {
      return `Use this page to hand off from ${reviewOriginLabel} into ${selectedTemplate.title.toLowerCase()} for ${incidentLabel} on ${selectedDeviceLabel}${guidedZoneId ? ` in ${zoneLabel}` : ''}.`;
    }
    return `Use this page to run ${selectedTemplate.title.toLowerCase()} for ${incidentLabel} on ${selectedDeviceLabel}${guidedZoneId ? ` in ${zoneLabel}` : ''}, then return to the same review flow to verify the result.`;
  }, [guidedIncidentType, guidedZoneId, reviewOriginLabel, selectedDeviceLabel, selectedTemplate]);

  const reviewScopeChips = useMemo(() => {
    const chips: string[] = [];
    if (guidedSiteId) chips.push(`Site: ${guidedSiteId}`);
    if (selectedDeviceId) chips.push(`Device: ${selectedDeviceLabel}`);
    if (reviewIncidentId) chips.push(`Incident: ${reviewIncidentId}`);
    if (reviewCaseId && reviewCaseId !== reviewIncidentId) chips.push(`Case: ${reviewCaseId}`);
    if (guidedZoneId) chips.push(`Zone: ${guidedZoneId}`);
    if (guidedIncidentType) chips.push(`Risk type: ${incidentTypeLabel(guidedIncidentType)}`);
    if (reviewProofSurface) chips.push(`Proof lane: ${reviewProofSurface}`);
    if (reviewProofPackageId) chips.push(`Package: ${reviewProofPackageId}`);
    if (guidedFromDate || guidedToDate) {
      chips.push(`Window: ${guidedFromDate || 'start'} -> ${guidedToDate || 'now'}`);
    }
    return chips;
  }, [
    guidedFromDate,
    guidedIncidentType,
    guidedSiteId,
    guidedToDate,
    guidedZoneId,
    reviewCaseId,
    reviewIncidentId,
    reviewProofPackageId,
    reviewProofSurface,
    selectedDeviceId,
    selectedDeviceLabel,
  ]);

  const openedFromIncidentReview = Boolean(
    requestedRunbookId
      || guidedSiteId
      || guidedZoneId
      || guidedIncidentType
      || guidedFromDate
      || guidedToDate
      || guidedWorkflow
      || reviewIncidentId
      || reviewCaseId
      || reviewProofPackageId
      || reviewSource,
  );

  const reviewFlowParams = useMemo<Record<string, any>>(
    () => ({
      device_id: selectedDeviceId || undefined,
      site_id: guidedSiteId || undefined,
      zone_id: guidedZoneId || undefined,
      incident_type: guidedIncidentType || undefined,
      from_date: guidedFromDate || undefined,
      to_date: guidedToDate || undefined,
      workflow: guidedWorkflow || undefined,
      incident_id: reviewIncidentId || undefined,
      case_id: reviewCaseId || undefined,
      proof_package_id: reviewProofPackageId || undefined,
      proof_surface: reviewProofSurface || undefined,
      source: reviewSource || undefined,
    }),
    [
      guidedFromDate,
      guidedIncidentType,
      guidedSiteId,
      guidedToDate,
      guidedWorkflow,
      guidedZoneId,
      reviewCaseId,
      reviewIncidentId,
      reviewProofPackageId,
      reviewProofSurface,
      reviewSource,
      selectedDeviceId,
    ],
  );

  const updateSelectedDeviceSearchParam = useCallback((deviceId: string, replace = false) => {
    const next = new URLSearchParams(searchParams);
    const normalized = String(deviceId || '').trim();
    if (normalized) {
      next.set('device_id', normalized);
    } else {
      next.delete('device_id');
    }
    // Remove legacy key so stale `device=` does not override future handoffs.
    next.delete('device');
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const canonicalDeviceId = String(searchParams.get('device_id') || '').trim();
    const legacyDeviceId = String(searchParams.get('device') || '').trim();
    if (!canonicalDeviceId && legacyDeviceId) {
      updateSelectedDeviceSearchParam(legacyDeviceId, true);
    }
  }, [searchParams, updateSelectedDeviceSearchParam]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    const syncAlias = (legacyKey: string, canonicalKey: string) => {
      const canonicalValue = String(next.get(canonicalKey) || '').trim();
      const legacyValue = String(next.get(legacyKey) || '').trim();
      if (!canonicalValue && legacyValue) {
        next.set(canonicalKey, legacyValue);
        changed = true;
      }
    };
    syncAlias('site', 'site_id');
    syncAlias('zone', 'zone_id');
    syncAlias('from', 'from_date');
    syncAlias('to', 'to_date');
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const nextFrom = toDateInputValue(guidedFromDate);
    const nextTo = toDateInputValue(guidedToDate);
    if (nextFrom) setEvidencePackFromDate(nextFrom);
    if (nextTo) setEvidencePackToDate(nextTo);
  }, [guidedFromDate, guidedToDate]);

  const loadEdgeHealth = useCallback(async () => {
    if (!selectedDeviceId) {
      setEdgeHealth(null);
      setEdgeHealthError(null);
      return;
    }
    setEdgeHealthLoading(true);
    setEdgeHealthError(null);
    try {
      const data = await fetchEdgeHealth(selectedDeviceId);
      setEdgeHealth(data);
    } catch (e: any) {
      setEdgeHealth(null);
      setEdgeHealthError(e?.response?.data?.error || e?.message || 'Failed to load device health');
    } finally {
      setEdgeHealthLoading(false);
    }
  }, [selectedDeviceId]);

  useEffect(() => {
    void loadEdgeHealth();
  }, [loadEdgeHealth]);

  useEffect(() => {
    const run = async () => {
      try {
        const res = await copilotApi.getEnabled();
        const en = Boolean(res.enabled);
        setEmbeddedEnabled(en);
        if (!en && mode === 'embedded_copilot') {
          setMode('external_agent_runtime');
          setOpsCopilotMode('external_agent_runtime');
        }
      } catch {
        setEmbeddedEnabled(false);
        if (mode === 'embedded_copilot') {
          setMode('external_agent_runtime');
          setOpsCopilotMode('external_agent_runtime');
        }
      }
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Initial load (server-first, local fallback).
    const run = async () => {
      try {
        const items = await listOpsRunbookExecutionsServer(50);
        setExecutions(items);
      } catch {
        setExecutions(listOpsCopilotExecutions());
      }
    };
    void run();
  }, []);

  useEffect(() => {
    const run = async () => {
      setTemplatesLoading(true);
      setTemplatesError(null);
      try {
        const items = await fetchOpsRunbookTemplates();
        setTemplates(items);
      } catch (e: any) {
        setTemplates([]);
        setTemplatesError(e?.response?.data?.error || e?.message || 'Failed to load runbook templates');
      } finally {
        setTemplatesLoading(false);
      }
    };
    void run();
  }, []);

  useEffect(() => {
    if (!requestedRunbookId || templatesLoading) return;
    const autoOpenKey = [
      requestedRunbookId,
      selectedDeviceId,
      guidedSiteId,
      guidedZoneId,
      guidedIncidentType,
      guidedFromDate,
      guidedToDate,
      guidedFocus,
      guidedWorkflow,
      reviewIncidentId,
      reviewCaseId,
      reviewProofPackageId,
      reviewProofSurface,
      reviewSource,
    ].join('|');
    if (autoOpenedRunbookRef.current === autoOpenKey) return;
    const template = templates.find((item) => item.id === requestedRunbookId);
    if (!template) return;
    autoOpenedRunbookRef.current = autoOpenKey;
    if (template.kind === 'guided') {
      const href = resolveGuidedRunbookHref(template, {
        ...reviewFlowParams,
        focus: guidedFocus || 'queue',
      } as any);
      if (href) {
        navigate(href);
      }
      return;
    }
    setDrawerPrefillParams((prev) => ({
      ...(prev || {}),
      ...reviewFlowParams,
    }));
    setDrawerTemplate(template);
    setDrawerOpen(true);
  }, [
    guidedFocus,
    guidedFromDate,
    guidedIncidentType,
    guidedToDate,
    guidedWorkflow,
    guidedZoneId,
    navigate,
    requestedRunbookId,
    selectedDeviceId,
    guidedSiteId,
    reviewCaseId,
    reviewIncidentId,
    reviewProofPackageId,
    reviewProofSurface,
    reviewSource,
    reviewFlowParams,
    templates,
    templatesLoading,
  ]);

  async function loadDevices() {
    setLoadingDevices(true);
    setDevicesError(null);
    try {
      const res = await api.get(endpoints.devices.list);
      const items: Device[] = Array.isArray(res.data?.devices) ? res.data.devices : [];
      setDevices(items);
      if (!selectedDeviceId && items.length > 0) {
        updateSelectedDeviceSearchParam(items[0].device_id, true);
      }
    } catch (e: any) {
      setDevicesError(e?.response?.data?.error || e?.message || 'Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  }

  useEffect(() => {
    void loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openExecution = useCallback(async (ex: OpsRunbookExecution) => {
    setSelectedExecution(ex);
    setExecDialogOpen(true);
    try {
      const response = await getOpsRunbookExecutionServer(ex.execution_id);
      const detailed = response?.execution;
      if (detailed) {
        setSelectedExecution(normalizeOpsRunbookExecution(detailed, ex));
      }
    } catch {
      // keep summary execution loaded from the recent history list
    }
  }, []);

  const refreshExecutions = async () => {
    try {
      const items = await listOpsRunbookExecutionsServer(50);
      setExecutions(items);
    } catch {
      setExecutions(listOpsCopilotExecutions());
    }
  };

  const selectedExecutionReviewContext = useMemo(() => {
    if (!selectedExecution) return null;
    const params = (selectedExecution.params || {}) as Record<string, any>;
    return {
      source: String(selectedExecution.source || params.source || '').trim(),
      incidentId: String(selectedExecution.incident_id || params.incident_id || '').trim(),
      caseId: String(selectedExecution.case_id || params.case_id || selectedExecution.incident_id || params.incident_id || '').trim(),
      proofPackageId: String(selectedExecution.proof_package_id || params.proof_package_id || '').trim(),
      proofSurface: String(selectedExecution.proof_surface || params.proof_surface || '').trim(),
      siteId: String(selectedExecution.site_id || params.site_id || '').trim(),
      zoneId: String(selectedExecution.zone_id || params.zone_id || '').trim(),
      incidentType: String(selectedExecution.incident_type || params.incident_type || '').trim(),
      fromDate: String(selectedExecution.from_date || params.from_date || '').trim(),
      toDate: String(selectedExecution.to_date || params.to_date || '').trim(),
      workflow: String(params.workflow || '').trim(),
      deviceId: String(selectedExecution.device_id || params.device_id || '').trim(),
    };
  }, [selectedExecution]);

  const selectedExecutionReturnHref = useMemo(() => {
    if (!selectedExecutionReviewContext) return null;
    if (
      selectedExecutionReviewContext.source === 'proof'
      && (
        selectedExecutionReviewContext.caseId
        || selectedExecutionReviewContext.proofPackageId
        || selectedExecutionReviewContext.proofSurface
      )
    ) {
      const params = new URLSearchParams();
      if (selectedExecutionReviewContext.caseId) params.set('case_id', selectedExecutionReviewContext.caseId);
      if (selectedExecutionReviewContext.incidentId) params.set('incident_id', selectedExecutionReviewContext.incidentId);
      if (selectedExecutionReviewContext.proofPackageId) params.set('proof_package_id', selectedExecutionReviewContext.proofPackageId);
      if (selectedExecutionReviewContext.proofSurface) params.set('surface', selectedExecutionReviewContext.proofSurface);
      return `/acceptance/proof?${params.toString()}`;
    }
    if (selectedExecutionReviewContext.incidentId) {
      return buildScopedIncidentsPath(
        `/incidents?incident_id=${encodeURIComponent(selectedExecutionReviewContext.incidentId)}&review=1`,
      );
    }
    return null;
  }, [selectedExecutionReviewContext]);

  const selectedExecutionReturnLabel = useMemo(
    () => (selectedExecutionReviewContext?.source === 'proof' ? 'Return to proof workspace' : 'Return to incident review'),
    [selectedExecutionReviewContext],
  );

  const selectedExecutionScopeChips = useMemo(() => {
    if (!selectedExecutionReviewContext) return [] as string[];
    const chips: string[] = [];
    if (selectedExecutionReviewContext.siteId) chips.push(`Site: ${selectedExecutionReviewContext.siteId}`);
    if (selectedExecutionReviewContext.deviceId) chips.push(`Device: ${selectedExecutionReviewContext.deviceId}`);
    if (selectedExecutionReviewContext.incidentId) chips.push(`Incident: ${selectedExecutionReviewContext.incidentId}`);
    if (
      selectedExecutionReviewContext.caseId
      && selectedExecutionReviewContext.caseId !== selectedExecutionReviewContext.incidentId
    ) {
      chips.push(`Case: ${selectedExecutionReviewContext.caseId}`);
    }
    if (selectedExecutionReviewContext.zoneId) chips.push(`Zone: ${selectedExecutionReviewContext.zoneId}`);
    if (selectedExecutionReviewContext.incidentType) {
      chips.push(`Risk type: ${incidentTypeLabel(selectedExecutionReviewContext.incidentType)}`);
    }
    if (selectedExecutionReviewContext.proofSurface) {
      chips.push(`Proof lane: ${selectedExecutionReviewContext.proofSurface}`);
    }
    if (selectedExecutionReviewContext.fromDate || selectedExecutionReviewContext.toDate) {
      chips.push(`Window: ${selectedExecutionReviewContext.fromDate || 'start'} -> ${selectedExecutionReviewContext.toDate || 'now'}`);
    }
    return chips;
  }, [selectedExecutionReviewContext]);

  const runTemplate = async (t: OpsRunbookTemplate) => {
    if (!canWriteAudit) {
      setRunError('You have read-only audit access. Runbook execution is disabled.');
      return;
    }
    setRunError(null);

    if (t.kind === 'guided') {
      const href = resolveGuidedRunbookHref(t, {
        ...reviewFlowParams,
        focus: guidedFocus || 'queue',
      });
      if (href) {
        navigate(href);
        return;
      }
      return;
    }

    if (t.requires_device && !selectedDeviceId) {
      setRunError('Select a device to run this template.');
      return;
    }

    setDrawerTemplate(t);
    setDrawerOpen(true);
  };

  const executeFromDrawer = async () => {
    const t = drawerTemplate;
    if (!t) return;
    if (!canWriteAudit) {
      setRunError('You have read-only audit access. Runbook execution is disabled.');
      return;
    }
    setRunError(null);
    setRunningId(t.id);
    const params: Record<string, any> = { ...(drawerPrefillParams || {}), ...drawerExtraParams };
    if (reason.trim()) params.reason = reason.trim();
    try {
      const ex = await runOpsRunbook(t, {
        device_id: params.device_id || selectedDeviceId || undefined,
        actor: user?.username || undefined,
        params,
      });
      await refreshExecutions();
      setDrawerOpen(false);
      setDrawerTemplate(null);
      setDrawerPrefillParams(null);
      setDrawerExtraParams({});
      openExecution(ex);
    } catch (e: any) {
      await refreshExecutions();
      setRunError(e?.response?.data?.error || e?.message || 'Runbook failed');
    } finally {
      setRunningId(null);
    }
  };

  const handleRecommendedAction = (action: RecommendedAction, oneClick = false) => {
    if (!canWriteAudit) {
      setRunError('You have read-only audit access. Runbook execution is disabled.');
      return;
    }
    const t = templates.find((x) => x.id === action.action_id);
    if (!t) return;
    const prefill: Record<string, any> = { ...reviewFlowParams, ...(action.default_inputs || {}) };
    if (action.why?.length) prefill.reason = action.why.join('; ');
    if (!prefill.device_id && selectedDeviceId) prefill.device_id = selectedDeviceId;
    if (oneClick) {
      setRunError(null);
      setRunningId(t.id);
      runOpsRunbook(t, {
        device_id: prefill.device_id || selectedDeviceId || undefined,
        actor: user?.username || undefined,
        params: prefill,
      })
        .then(async (ex) => {
          await refreshExecutions();
          openExecution(ex);
        })
        .catch((e: any) => {
          setRunError(e?.response?.data?.error || e?.message || 'Runbook failed');
        })
        .finally(() => setRunningId(null));
    } else {
      setDrawerPrefillParams(prefill);
      if (prefill.reason) setReason(prefill.reason);
      runTemplate(t);
    }
  };

  const generateEvidencePack = async () => {
    setEvidencePackError(null);
    setEvidencePackResult(null);
    setEvidencePackLoading(true);
    try {
      const fromDate = `${evidencePackFromDate}T00:00:00.000Z`;
      const toDate = `${evidencePackToDate}T23:59:59.999Z`;
      const d = devices.find((x) => x.device_id === selectedDeviceId);
      const siteId = (d as any)?.site_id || 'default';
      const res = await reportsApi.getEvidencePack({
        from_date: fromDate,
        to_date: toDate,
        site_id: siteId,
      });
      setEvidencePackResult(res);
    } catch (e: any) {
      setEvidencePackError(e?.response?.data?.error || e?.message || 'Failed to generate evidence pack');
    } finally {
      setEvidencePackLoading(false);
    }
  };

  const downloadEvidencePack = async () => {
    setEvidencePackError(null);
    try {
      const fromDate = `${evidencePackFromDate}T00:00:00.000Z`;
      const toDate = `${evidencePackToDate}T23:59:59.999Z`;
      const d = devices.find((x) => x.device_id === selectedDeviceId);
      const siteId = (d as any)?.site_id || 'default';
      const { blob, filename } = await reportsApi.downloadEvidencePack({
        from_date: fromDate,
        to_date: toDate,
        site_id: siteId,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setEvidencePackError(e?.response?.data?.error || e?.message || 'Failed to download evidence pack');
    }
  };

  const downloadTrustPack = async () => {
    setTrustPackError(null);
    setTrustPackLoading(true);
    try {
      const fromDate = `${evidencePackFromDate}T00:00:00.000Z`;
      const toDate = `${evidencePackToDate}T23:59:59.999Z`;
      const d = devices.find((x) => x.device_id === selectedDeviceId);
      const siteId = (d as any)?.site_id || 'default';
      const { blob, filename } = await reportsApi.downloadTrustPack({
        from_date: fromDate,
        to_date: toDate,
        site_id: siteId,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setTrustPackError(e?.response?.data?.error || e?.message || 'Failed to download trust pack');
    } finally {
      setTrustPackLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RunbookIcon color="primary" />
            Ops Copilot Runbooks
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 840 }}>
            Deterministic templates that map to existing control-plane endpoints. This is the UI wrapper for the same “agentic”
            tool chains you demo via MCP.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => void refreshExecutions()}>
            Refresh history
          </Button>
        </Stack>
      </Box>

      {openedFromIncidentReview ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button color="inherit" size="small" startIcon={<BackIcon />} onClick={() => navigate(reviewReturnHref)}>
                {reviewReturnLabel}
              </Button>
              {selectedDeviceId ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate(`/diagnostics?device=${encodeURIComponent(selectedDeviceId)}`)}
                >
                  Open diagnostics
                </Button>
              ) : null}
            </Stack>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {reviewHandoffTitle}
          </Typography>
          <Typography variant="body2">
            {reviewHandoffSummary || 'This page continues the follow-up work suggested by the incident review workspace.'}
          </Typography>
          {reviewScopeChips.length ? (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.25 }}>
              {reviewScopeChips.map((chip) => (
                <Chip key={chip} size="small" label={chip} variant="outlined" />
              ))}
            </Stack>
          ) : null}
        </Alert>
      ) : null}

      <Paper sx={{ p: 2, mb: 2 }}>
        {openedFromIncidentReview ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              What this page does
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Runbooks fix the operational problem behind the review finding, such as missing proof uploads or device-side evidence issues. Return to the same case review flow when you want to judge the evidence, resolve the case, or continue the operator narrative.
            </Typography>
          </Alert>
        ) : null}
        <Box display="flex" alignItems="center" justifyContent="space-between" onClick={() => setModeExpanded((x) => !x)} sx={{ cursor: 'pointer' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Mode / Embedded Copilot
          </Typography>
          <IconButton size="small">{modeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>
        <Collapse in={modeExpanded}>
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select
                  value={mode}
                  label="Mode"
                  onChange={(e) => {
                    const next = String(e.target.value || '') as OpsCopilotMode;
                    setMode(next);
                    setOpsCopilotMode(next);
                  }}
                >
                  <MenuItem value="external_agent_runtime">External agent runtime (default)</MenuItem>
                  {embeddedEnabled ? (
                    <MenuItem value="embedded_copilot">Embedded Copilot (opt-in)</MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={7}>
              {mode === 'external_agent_runtime' ? (
                <Alert severity="info">
                  Edgentik provides tools + audit hooks. Use Claude Code / Cursor (MCP client) to do LLM planning. No model keys
                  required in Edgentik.
                </Alert>
              ) : (
                <Alert severity="warning">
                  Embedded Copilot is an opt-in mode and is not enabled by default. It requires model credentials and strict RBAC +
                  confirmations. (This UI toggle is the first step; model execution wiring comes next.)
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(String(e.target.value || ''))}
                placeholder="E.g. applied drift after policy deploy; want to re-sync desired state."
                helperText="Recorded in the audit trail for runbook executions."
              />
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
              <FormControl fullWidth size="small" disabled={loadingDevices}>
                <InputLabel>Select device</InputLabel>
                <Select
                  value={selectedDeviceId}
                  label="Select device"
                  onChange={(e) => updateSelectedDeviceSearchParam(String(e.target.value || ''))}
                >
                  {devices.map((d) => (
                    <MenuItem key={d.device_id} value={d.device_id}>
                      {d.name} ({d.device_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedDeviceId && (
                <Tooltip title="Refresh device health">
                  <IconButton size="small" onClick={() => void loadEdgeHealth()} disabled={edgeHealthLoading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack direction="row" gap={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button variant="outlined" onClick={() => navigate(selectedDeviceId ? `/preflight?device=${encodeURIComponent(selectedDeviceId)}` : '/preflight')}>
                Preflight
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(selectedDeviceId ? `/devices/${encodeURIComponent(selectedDeviceId)}/control` : '/devices/control')}
              >
                Device Control
              </Button>
              <Button variant="outlined" onClick={() => navigate(selectedDeviceId ? `/bringup?device=${encodeURIComponent(selectedDeviceId)}` : '/bringup')}>
                Bring-up
              </Button>
            </Stack>
          </Grid>
        </Grid>
        {devicesError && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {devicesError}
          </Alert>
        )}

        {selectedDeviceId && edgeHealth?.reason_codes?.includes('device_offline') && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Blocked: device offline</strong>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {edgeHealth?.error || 'Edge device not reachable'}. Restore reachability before running device-scoped runbooks.
            </Typography>
          </Alert>
        )}
        {selectedDeviceId && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HealthIcon fontSize="small" />
              Device Health Summary
            </Typography>
            {!edgeHealth?.reason_codes?.includes('device_offline') &&
              Array.isArray(edgeHealth?.reason_codes) &&
              (edgeHealth?.reason_codes?.includes('config_drift') ||
                edgeHealth?.reason_codes?.includes('release_drift') ||
                edgeHealth?.reason_codes?.includes('firmware_drift')) && (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                {edgeHealth?.reason_codes?.includes('config_drift') && (
                  <Chip size="small" label="Config drift" variant="outlined" color="warning" />
                )}
                {edgeHealth?.reason_codes?.includes('release_drift') && (
                  <Chip size="small" label="Release drift" variant="outlined" color="warning" />
                )}
                {edgeHealth?.reason_codes?.includes('firmware_drift') && (
                  <Chip size="small" label="Firmware drift" variant="outlined" color="warning" />
                )}
              </Stack>
            )}
            {edgeHealthLoading ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Stack>
            ) : edgeHealthError ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>{edgeHealthError}</Alert>
            ) : edgeHealth?.ok === false ? (
              <Alert severity="warning" sx={{ py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {edgeHealth.error || 'edge_health_error'}
                </Typography>
                {edgeHealth.detail && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {edgeHealth.detail}
                  </Typography>
                )}
              </Alert>
            ) : edgeHealth ? (
              <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                <Chip
                  size="small"
                  label={String(edgeHealth.status || 'unknown')}
                  color={String(edgeHealth.status || '').toLowerCase() === 'healthy' ? 'success' : 'warning'}
                  variant="outlined"
                />
                {Array.isArray(edgeHealth.reason_codes_structured) && edgeHealth.reason_codes_structured.length > 0 ? (
                  <>
                    {edgeHealth.reason_codes_structured
                      .slice()
                      .sort((a, b) => {
                        const rank = (s: any) => (String(s || '') === 'high' ? 0 : String(s || '') === 'medium' ? 1 : 2);
                        return rank(a.severity) - rank(b.severity);
                      })
                      .map((rc) => {
                        const sev = String(rc.severity || '').toLowerCase();
                        const color = sev === 'high' ? 'error' : sev === 'medium' ? 'warning' : 'default';
                        const tipParts = [
                          `severity=${rc.severity}`,
                          `resource=${rc.resource_type}${rc.resource_id ? `:${rc.resource_id}` : ''}`,
                          rc.details ? `details=${JSON.stringify(rc.details)}` : null,
                        ].filter(Boolean);
                        return (
                          <Tooltip key={`${rc.code}:${rc.resource_id || ''}`} title={tipParts.join(' • ')}>
                            <Chip size="small" label={rc.code} variant="outlined" color={color as any} />
                          </Tooltip>
                        );
                      })}
                  </>
                ) : Array.isArray(edgeHealth.reason_codes) && edgeHealth.reason_codes.length > 0 ? (
                  <>
                    {edgeHealth.reason_codes.map((c) => (
                      <Chip key={c} size="small" label={c} variant="outlined" />
                    ))}
                  </>
                ) : null}
                {edgeHealth.ip_address && (
                  <Typography variant="caption" color="text.secondary">IP: {edgeHealth.ip_address}</Typography>
                )}
                <Button size="small" variant="text" onClick={() => navigate(selectedDeviceId ? `/diagnostics?device=${encodeURIComponent(selectedDeviceId)}` : '/diagnostics')}>
                  Diagnostics
                </Button>
                <Button size="small" variant="text" onClick={() => navigate(selectedDeviceId ? `/devices/${encodeURIComponent(selectedDeviceId)}/control` : '/devices/control')}>
                  Device Control
                </Button>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No health data</Typography>
            )}
          </Box>
        )}
      </Paper>

      {selectedDeviceId && (edgeHealth?.recommended_actions?.length ?? 0) > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Recommended Actions</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            {edgeHealth?.recommended_actions?.map((a) => (
              <React.Fragment key={a.action_id}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={runningId === a.action_id ? <CircularProgress size={14} color="inherit" /> : <PlayIcon />}
                  onClick={() => handleRecommendedAction(a, true)}
                  disabled={!canWriteAudit || !!runningId}
                >
                  {runningId === a.action_id ? 'Running…' : a.label}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleRecommendedAction(a, false)}
                  disabled={!canWriteAudit || !!runningId}
                  sx={{ minWidth: 0, px: 0.5 }}
                >
                  Review plan
                </Button>
              </React.Fragment>
            ))}
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" onClick={() => setEvidencePackExpanded((x) => !x)} sx={{ cursor: 'pointer' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EvidencePackIcon fontSize="small" />
            Evidence Pack (cloud report)
          </Typography>
          <IconButton size="small">{evidencePackExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>
        <Collapse in={evidencePackExpanded}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cloud-generated JSON/ZIP bundle with incidents rollup, reliability proof pack, and reliability bundle. Includes manifest_sha256, per-artifact hashes, and retention policy.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="From date"
                type="date"
                value={evidencePackFromDate}
                onChange={(e) => setEvidencePackFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="To date"
                type="date"
                value={evidencePackToDate}
                onChange={(e) => setEvidencePackToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                startIcon={evidencePackLoading ? <CircularProgress size={16} color="inherit" /> : <EvidencePackIcon />}
                onClick={() => void generateEvidencePack()}
                disabled={evidencePackLoading}
              >
                {evidencePackLoading ? 'Generating…' : 'Generate Evidence Pack'}
              </Button>
            </Grid>
          </Grid>
          {evidencePackError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setEvidencePackError(null)}>
              {evidencePackError}
            </Alert>
          )}
          {evidencePackResult?.ok && evidencePackResult.evidence_pack && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <ReportVisuals visuals={evidencePackResult.report_visuals} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                manifest_sha256: {evidencePackResult.evidence_pack.signed_hashes?.manifest_sha256?.slice(0, 16)}…
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                expires_at: {evidencePackResult.evidence_pack.retention_policy?.expires_at?.slice(0, 19) || '—'}
              </Typography>
              <Button size="small" startIcon={<DownloadIcon />} onClick={() => void downloadEvidencePack()} sx={{ mt: 1 }}>
                Download ZIP
              </Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <EvidencePackIcon fontSize="small" />
            Customer handoff bundle
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Full customer-facing handover ZIP (the repo-root <code>evidence-pack/&lt;tag&gt;/</code> bundle: SBOM, scan envelopes, runbooks, and the latest SLO evidence when available).
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Includes Epic 10 on-call readiness: game-day suite results, MTTR trend, and runbooks (top-20 failure modes, postmortem template).
          </Typography>
          {trustPackError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setTrustPackError(null)}>
              {trustPackError}
            </Alert>
          )}
          <Button
            variant="contained"
            color="secondary"
            startIcon={trustPackLoading ? <CircularProgress size={16} color="inherit" /> : <EvidencePackIcon />}
            onClick={() => void downloadTrustPack()}
            disabled={trustPackLoading}
            sx={{ mt: 1 }}
          >
            {trustPackLoading ? 'Building…' : 'Generate + Download Customer Handoff Bundle'}
          </Button>
        </Collapse>
      </Paper>

      {runError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRunError(null)}>
          {runError}
        </Alert>
      )}

      {!canWriteAudit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only audit access. Runbook execution is disabled.
        </Alert>
      )}

      {templatesError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTemplatesError(null)}>
          {templatesError}
        </Alert>
      )}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2}>
            {templatesLoading && (
              <Grid item xs={12}>
                <Alert severity="info">Loading runbook templates…</Alert>
              </Grid>
            )}
            {templates.map((t) => {
              const isRunning = runningId === t.id;
              const isGuided = t.kind === 'guided';
              return (
                <Grid item xs={12} md={6} key={t.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box>
                          <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                            {t.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t.summary}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={isGuided ? 'guided' : 'execute'}
                          color={isGuided ? 'default' : 'primary'}
                          variant={isGuided ? 'outlined' : 'filled'}
                        />
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        {typeof t.estimated_sec === 'number' ? `ETA: ~${Math.round(t.estimated_sec / 60)} min` : 'ETA: —'}
                        {t.requires_device ? ' • device-scoped' : ' • fleet-scoped'}
                      </Typography>
                      {!isGuided && Array.isArray(t.steps) && t.steps.length > 0 && (
                        <List dense sx={{ mt: 1 }}>
                          {t.steps.map((s) => (
                            <ListItem key={s.id} disableGutters sx={{ py: 0.25 }}>
                              <ListItemText
                                primary={<Typography variant="body2">{s.title}</Typography>}
                                secondary={s.detail}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-start', px: 2, pb: 2 }}>
                      {isGuided && t.requires_device && !selectedDeviceId ? (
                        <Tooltip title="Select a device to run this runbook">
                          <span>
                            <Button variant="outlined" startIcon={<OpenIcon />} disabled>
                              Open
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          variant={isGuided ? 'outlined' : 'contained'}
                          startIcon={isGuided ? <OpenIcon /> : <PlayIcon />}
                          onClick={() => void runTemplate(t)}
                          disabled={!canWriteAudit || isRunning}
                        >
                          {isRunning ? 'Running…' : isGuided ? 'Open' : 'Run'}
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 88 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Recent executions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Shared history (server-side) when available. If the server endpoint is unavailable, this page falls back to local history.
            </Typography>

            {executions.length === 0 ? (
              <Alert severity="info">No executions yet. Run a template to generate an audit record.</Alert>
            ) : (
              <List dense>
                {executions.slice(0, 12).map((ex) => (
                  <ListItem
                    key={ex.execution_id}
                    disableGutters
                    secondaryAction={
                      <Button size="small" variant="text" onClick={() => openExecution(ex)}>
                        View
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ex.runbook_title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {ex.status} • {ex.device_id ? `device=${ex.device_id}` : 'fleet'} •{' '}
                          {new Date(ex.started_at).toLocaleString()}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          if (!runningId) {
            setDrawerOpen(false);
            setDrawerTemplate(null);
            setDrawerPrefillParams(null);
            setDrawerExtraParams({});
          }
        }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            Run runbook
          </Typography>
          {drawerTemplate && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {drawerTemplate.title}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {drawerTemplate.summary}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Plan
              </Typography>
              <List dense sx={{ mb: 2, flex: 1, overflow: 'auto' }}>
                {(drawerTemplate.steps || []).map((s) => (
                  <ListItem key={s.id} disableGutters sx={{ py: 0.25 }}>
                    <ListItemText primary={s.title} secondary={s.detail} />
                  </ListItem>
                ))}
                {(!drawerTemplate.steps || drawerTemplate.steps.length === 0) && (
                  <ListItem disableGutters>
                    <ListItemText secondary="Execute runbook steps via control-plane API." />
                  </ListItem>
                )}
              </List>
              {(drawerTemplate.id === 'device_update_complete' || drawerTemplate.id === 'bring_to_desired_state') && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Desired release ID {drawerTemplate.id === 'device_update_complete' ? '(required)' : '(optional — fetched from device if empty)'}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={drawerExtraParams.desired_release_id || ''}
                    onChange={(e) => setDrawerExtraParams((p) => ({ ...p, desired_release_id: e.target.value.trim() }))}
                    placeholder="e.g. v1.0.0"
                    sx={{ mb: 2 }}
                  />
                </>
              )}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Reason (optional)
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={reason}
                onChange={(e) => setReason(String(e.target.value || ''))}
                placeholder="E.g. applied drift; re-sync desired state"
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 'auto', pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDrawerOpen(false);
                    setDrawerTemplate(null);
                    setDrawerExtraParams({});
                  }}
                  disabled={!!runningId}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={runningId === drawerTemplate.id ? <CircularProgress size={16} /> : <PlayIcon />}
                  onClick={() => void executeFromDrawer()}
                  disabled={
                    !canWriteAudit ||
                    !!runningId ||
                    (drawerTemplate.id === 'device_update_complete' && !String(drawerExtraParams.desired_release_id || '').trim())
                  }
                >
                  {runningId === drawerTemplate.id ? 'Running…' : 'Execute'}
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Drawer>

      <Dialog open={execDialogOpen} onClose={() => setExecDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Explain → Execute → Audit</DialogTitle>
        <DialogContent>
          {!selectedExecution ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Alert severity={selectedExecution.status === 'succeeded' ? 'success' : selectedExecution.status === 'failed' ? 'error' : 'info'} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {selectedExecution.runbook_title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  execution_id={selectedExecution.execution_id} • status={selectedExecution.status} • actor={selectedExecution.actor || 'unknown'}
                </Typography>
              </Alert>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Reason
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {String((selectedExecution?.params as any)?.reason || '').trim()
                  ? String((selectedExecution?.params as any)?.reason || '').trim()
                  : 'Operator-triggered runbook execution from Ops Copilot UI.'}
              </Typography>

              {selectedExecutionScopeChips.length ? (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Case context
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                    {selectedExecutionScopeChips.map((chip) => (
                      <Chip key={chip} size="small" label={chip} variant="outlined" />
                    ))}
                  </Stack>
                </>
              ) : null}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Plan
              </Typography>
              <List dense sx={{ mb: 2 }}>
                {(selectedExecution.steps || []).map((s) => (
                  <ListItem key={s.id} disableGutters>
                    <ListItemText
                      primary={s.title}
                      secondary={stepResultSummary(s.id, s.result ?? {})}
                    />
                  </ListItem>
                ))}
                {(!selectedExecution.steps || selectedExecution.steps.length === 0) && (
                  <ListItem disableGutters>
                    <ListItemText secondary="No explicit steps (guided runbook)." />
                  </ListItem>
                )}
              </List>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Execute
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedExecution.status === 'succeeded'
                  ? 'All steps completed successfully.'
                  : selectedExecution.status === 'failed'
                  ? 'One or more steps failed.'
                  : 'Execution in progress.'}
              </Typography>
              <Box display="flex" alignItems="center" onClick={() => setExecutionJsonExpanded((x) => !x)} sx={{ cursor: 'pointer', mb: 2 }}>
                <IconButton size="small">{executionJsonExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                <Typography variant="caption" color="text.secondary">
                  {executionJsonExpanded ? 'Hide' : 'Show'} technical details (JSON)
                </Typography>
              </Box>
              {executionJsonExpanded && (
                <Box
                  component="pre"
                  sx={{
                    fontSize: 11,
                    fontFamily: 'monospace',
                    background: '#0b1020',
                    color: '#e6edf3',
                    padding: 2,
                    borderRadius: 1,
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxHeight: 280,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    mb: 2,
                  }}
                >
                  {JSON.stringify(selectedExecution, null, 2)}
                </Box>
              )}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Audit
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Execution record is persisted server-side in the audit log and shared across operators.
              </Typography>
              {(selectedExecution as any).guardrails_log_ref && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Guardrails: {(selectedExecution as any).guardrails_log_ref}
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<AuditIcon />}
                onClick={() =>
                  navigate(
                    `/audit?resource_type=ops_runbook&resource_id=${encodeURIComponent(selectedExecution.execution_id)}`
                  )
                }
              >
                View full audit
              </Button>
              {(() => {
                const followOn = (selectedExecution.steps || []).find(
                  (s) => (s.id || (s as any).step_id) === 'run_preflight'
                );
                const data = followOn?.result?.data ?? (followOn as any)?.data;
                const report = data?.report ?? data;
                const f = report?.follow_on_report;
                if (!f?.runbook_template_id || !f?.params) return null;
                const nextTemplate = templates.find((t) => t.id === f.runbook_template_id);
                if (!nextTemplate) return null;
                return (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlayIcon />}
                    sx={{ mt: 1 }}
                    onClick={() => {
                      setExecDialogOpen(false);
                      setDrawerPrefillParams(f.params);
                      setDrawerTemplate(nextTemplate);
                      setDrawerOpen(true);
                    }}
                  >
                    Run next step: {nextTemplate.title}
                  </Button>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExecDialogOpen(false)}>Close</Button>
          {selectedExecutionReturnHref ? (
            <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate(selectedExecutionReturnHref)}>
              {selectedExecutionReturnLabel}
            </Button>
          ) : null}
          {selectedExecution?.device_id && (
            <Button
              variant="outlined"
              onClick={() => navigate(`/devices/${encodeURIComponent(selectedExecution.device_id || '')}/control`)}
            >
              Open Device Control
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => {
              refreshExecutions();
              setExecDialogOpen(false);
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpsCopilotRunbooks;
